import React, { useMemo } from "react";
import { useQuery } from "urql";
import { sortBy } from "lodash";

import { Grid, Column } from "../../../../components/src";
import { ScatterChart } from "../../../../components/src/charts/ScatterChart";
import { FoNavInstrumentName } from "./FoNavInstrumentName";
import { GET_FONAV_CURVE } from "./queries";
import {
    FoNavGridRow,
    getFixedLegMaturityPeriod,
    getTimeSeriesValueAtDate,
    maturityPeriodToYears,
    toBpChangeGridValue,
    toPercentGridValue
} from "./utils";

interface FoNavCurveProps {
    curveDefinitionId: string;
    header: string;
    loadingLabel: string;
    startDate: string;
    endDate: string;
}

interface CurveChartPoint {
    instrumentId: string;
    name: string;
    maturityPeriod: string;
    timeToMaturityYears: number;
    endRatePercent: number;
}

export function FoNavCurve({
    curveDefinitionId,
    header,
    loadingLabel,
    startDate,
    endDate
}: FoNavCurveProps): React.ReactElement {
    const [{ fetching, error, data }] = useQuery({
        query: GET_FONAV_CURVE,
        variables: {
            curveId: curveDefinitionId,
            startDate,
            endDate
        },
        pause: !curveDefinitionId || !startDate || !endDate,
        requestPolicy: "network-only"
    });

    const tableRows = useMemo((): FoNavGridRow[] => {
        const instruments = data?.curveDefinition?.definition?.instruments;
        if (!instruments) {
            return [];
        }

        const rows = instruments
            .map((item) => {
                const instrument = item.instrument;
                if (!instrument) {
                    return null;
                }

                const maturityPeriod = getFixedLegMaturityPeriod(instrument.model?.legs);
                const startRaw = getTimeSeriesValueAtDate(instrument.startSeries, startDate);
                const endRaw = getTimeSeriesValueAtDate(instrument.endSeries, endDate);
                const timeToMaturityYears = maturityPeriod ? maturityPeriodToYears(maturityPeriod) : null;

                const row: FoNavGridRow & { timeToMaturityYears: number | null } = {
                    name: instrument.name,
                    instrumentId: instrument._id,
                    startValue: toPercentGridValue(startRaw),
                    endValue: toPercentGridValue(endRaw),
                    change: toBpChangeGridValue(startRaw, endRaw),
                    timeToMaturityYears
                };
                return row;
            })
            .filter((row): row is FoNavGridRow & { timeToMaturityYears: number | null } => row !== null);

        return sortBy(rows, (row) => row.timeToMaturityYears ?? Number.MAX_SAFE_INTEGER).map(
            ({ timeToMaturityYears: _timeToMaturityYears, ...row }) => row
        );
    }, [data, startDate, endDate]);

    const chartPoints = useMemo((): CurveChartPoint[] => {
        const instruments = data?.curveDefinition?.definition?.instruments;
        if (!instruments) {
            return [];
        }

        const points: CurveChartPoint[] = [];

        for (const item of instruments) {
            const instrument = item.instrument;
            if (!instrument) {
                continue;
            }

            const maturityPeriod = getFixedLegMaturityPeriod(instrument.model?.legs);
            const timeToMaturityYears = maturityPeriod ? maturityPeriodToYears(maturityPeriod) : null;
            const endRaw = getTimeSeriesValueAtDate(instrument.endSeries, endDate);

            if (timeToMaturityYears === null || endRaw === null) {
                continue;
            }

            points.push({
                instrumentId: instrument._id,
                name: instrument.name,
                maturityPeriod,
                timeToMaturityYears,
                endRatePercent: endRaw * 100
            });
        }

        return sortBy(points, "timeToMaturityYears");
    }, [data, endDate]);

    if (fetching) {
        return <p>Loading {loadingLabel}...</p>;
    }

    if (error) {
        return <p>error: {JSON.stringify(error, null, 2)}</p>;
    }

    return (
        <div className="mb-4">
            {data?.curveDefinition ? (
                <p className="text-muted mb-3">
                    {data.curveDefinition.name} · {data.curveDefinition.type} · {data.curveDefinition.definition?.currency}
                </p>
            ) : null}

            <div className="flex flex-wrap -mx-2">
                <div className="w-full lg:w-6/12 md:w-6/12 px-2 mb-4">
                    <Grid header={header} data={tableRows} className="table-timeseries" tableClassName="table-xs" sortable>
                        <Column
                            field="name"
                            className="nowrap left"
                            format={(value: string, item: FoNavGridRow) => (
                                <FoNavInstrumentName name={value} instrumentId={item?.instrumentId} />
                            )}
                        />
                        <Column field="startValue" title="Start value (%)" className="nowrap left" />
                        <Column field="endValue" title="End value (%)" />
                        <Column field="change" title="Change (bp)" />
                    </Grid>
                </div>
                <div className="w-full lg:w-6/12 md:w-6/12 px-2 mb-4">
                    {chartPoints.length === 0 ? (
                        <p>No plottable curve points found for {endDate}.</p>
                    ) : (
                        <ScatterChart
                            points={chartPoints.map((point) => ({
                                name: `${point.name} (${point.maturityPeriod})`,
                                x: point.timeToMaturityYears,
                                y: point.endRatePercent
                            }))}
                            connect
                            xLabel="Time to maturity (years)"
                            yLabel="Rate (%)"
                            xFormat={(value) => value.toFixed(2)}
                            yFormat={(value) => value.toFixed(2) + "%"}
                            ariaLabel={data?.curveDefinition?.name ?? header}
                            height={420}
                            style={{ width: "100%", height: "50vh" }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
